import React, { useEffect, useState } from "react";
import { Content } from "../../components";
import TransmissionsChart from "./TransmissionsChart";
import BillableChart from "./BillableChart";
import MetricCards, { Metric } from "./MetricCards";
import { useDashboardAnalytics } from "../../hooks/patients";
import styles from "./RTMDashboard.module.css";
import BillablePatientsTable from "./BillablePatientsTable";
import BillingTrendsChart from "./BillingTrendsChart";
import useBillingCycles from "../../hooks/billing/useDashboardBillingStatus";
import "../../styles/PatientsDashboard.css";
import useFloatingTimer from "../../hooks/patients/useFloatingTimer";
import FloatingTimer from "../../components/AccordionRow/FloatingTimer";

const RTMDashboard: React.FC = () => {
  const { analyticsData, getDashboardAnalytics, analyticsDataLoading } =
    useDashboardAnalytics();
  const {
    billingCycles,
    getBillingCycles,
    loading: billingCyclesLoading,
  } = useBillingCycles();

  // Floating Timer
  const [showFloatingTimer, setShowFloatingTimer] = useState(false);

  const { floatingTimer, getFloatingTimer } = useFloatingTimer();

  useEffect(() => {
    getDashboardAnalytics();
    getBillingCycles();
    getFloatingTimer();
  }, []);

  // visibility when floatingTimer changes
  useEffect(() => {
    if (floatingTimer) {
      setShowFloatingTimer(floatingTimer.isAlreadyStarted === true);
    }
  }, [floatingTimer]);

  const transformMetrics = (data: any): Metric[] => {
    if (!data) return [];

    return [
      {
        title: "Total Patients",
        value: data.totalPatients?.toString() || "0",
        percentage: data.totalPatientsPercentage || 0,
        chartData: {
          labels: ["1", "2", "3", "4", "5", "6"],
          datasets: [
            {
              data: data.totalPatientsTrend || [0, 0, 0, 0, 0, 0],
              borderColor: "#a073d5",
              tension: 0.4,
              fill: false,
              borderWidth: 2,
            },
          ],
        },
      },
      {
        title: "Active Monitoring",
        value: data.activeMonitoring?.toString() || "0",
        percentage: data.activeMonitoringPercentage || 0,
        chartData: {
          labels: ["1", "2", "3", "4", "5", "6"],
          datasets: [
            {
              data: data.activeMonitoringTrend || [0, 0, 0, 0, 0, 0],
              borderColor: "#a073d5",
              tension: 0.4,
              fill: false,
              borderWidth: 2,
            },
          ],
        },
      },
      {
        title: "Critical Alerts",
        value: data.criticalAlerts?.toString() || "0",
        percentage: data.criticalAlertsPercentage || 0,
        chartData: {
          labels: ["1", "2", "3", "4", "5", "6"],
          datasets: [
            {
              data: data.criticalAlertsTrend || [0, 0, 0, 0, 0, 0],
              borderColor: "#a073d5",
              tension: 0.4,
              fill: false,
              borderWidth: 2,
            },
          ],
        },
      },
      {
        title: "Total Clinics",
        value: data.totalAssociatedClinics?.toString() || "0",
        percentage: data.totalAssociatedClinicsPercentage || 0,
        chartData: {
          labels: ["1", "2", "3", "4", "5", "6"],
          datasets: [
            {
              data: data.totalAssociatedClinicsTrend || [0, 0, 0, 0, 0, 0],
              borderColor: "#a073d5",
              tension: 0.4,
              fill: false,
              borderWidth: 2,
            },
          ],
        },
      },
    ];
  };

  const metrics = transformMetrics(analyticsData);

  return (
    <div className={styles.dashboardContainer}>
      <MetricCards metrics={metrics} loading={analyticsDataLoading} />

      <div className="middle-section">
        <div className="chart-container">
          <BillingTrendsChart
            billingCycles={billingCycles}
            loading={billingCyclesLoading}
          />
        </div>
        <div className="chart-container">
          <TransmissionsChart />
        </div>
      </div>

      <div className={styles.tableSection}>
        <div>
          <BillablePatientsTable />
        </div>
        <div>
          <BillableChart />
        </div>
      </div>
      {showFloatingTimer && floatingTimer && (
        <FloatingTimer floatingTimer={floatingTimer} />
      )}
    </div>
  );
};

export default RTMDashboard;
